/*
 * THE FROZEN WASTE'S SPIRES. Asked for on 2026-10-08: "make all the snow
 * terrain touching the northern side of the map \"frozen waste\", spiky
 * looking ice mountains that are impassable and have no spawns".
 *
 * The waste is a mass of ice a storey tall, drawn the way rock is (see
 * oblique.ts), with its top and faces in the ice's own picture (drawTile's
 * Tile.FrozenWaste). On top of that, in the oblique view, each tile stands
 * a cluster of spikes of ice up off the top of the mass: two to four
 * shards, leaning a little, lit on the west and shadowed on the east, so a
 * stretch of it reads as a ridge of ice teeth rather than as a flat
 * plateau of pale rock. The variant picks the cluster, so the ridge is not
 * one spike repeated.
 *
 * It is kept in a file of its own and imports nothing, so neither the
 * sprites nor the renderer have to grow round it.
 */

/** The picture's size, in world pixels: a tile wide and a little over, and tall enough for the highest spike. */
export const SPIRE_W = 40;
export const SPIRE_H = 44;
/** Where the middle of the cluster's foot is in the picture, from its top-left. */
export const SPIRE_FOOT = { x: SPIRE_W / 2, y: SPIRE_H - 6 };

const ICE = { lit: "#eef6fc", mid: "#c4dcec", shade: "#8fb3cc", edge: "#5f84a0", glint: "#ffffff" };

/** One shard: the middle of its foot from the cluster's, its half-width at the foot, its height, and how far its tip leans east. */
interface Shard {
  dx: number;
  dy: number;
  half: number;
  h: number;
  lean: number;
}

/** The clusters, by variant: the tallest shard nearest the back, so the front ones stand against it. */
const CLUSTERS: Shard[][] = [
  [
    { dx: -4, dy: -3, half: 6, h: 34, lean: 1 },
    { dx: 7, dy: 0, half: 5, h: 22, lean: 2 },
    { dx: -10, dy: 2, half: 4, h: 15, lean: -1 },
  ],
  [
    { dx: 3, dy: -4, half: 7, h: 30, lean: -2 },
    { dx: -8, dy: 0, half: 5, h: 24, lean: 1 },
    { dx: 10, dy: 2, half: 4, h: 13, lean: 1 },
    { dx: 0, dy: 3, half: 4, h: 11, lean: 0 },
  ],
  [
    { dx: 0, dy: -2, half: 7, h: 36, lean: 0 },
    { dx: -9, dy: 2, half: 4, h: 17, lean: -2 },
  ],
  [
    { dx: -6, dy: -3, half: 5, h: 26, lean: 2 },
    { dx: 6, dy: -2, half: 6, h: 31, lean: -1 },
    { dx: 0, dy: 3, half: 4, h: 14, lean: 1 },
  ],
];

type Ctx = CanvasRenderingContext2D;

const spires = new Map<number, HTMLCanvasElement>();

/** The cluster of spikes for a variant, made once and kept. */
export function iceSpirePicture(variant: number): HTMLCanvasElement {
  const v = ((variant % CLUSTERS.length) + CLUSTERS.length) % CLUSTERS.length;
  let c = spires.get(v);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = SPIRE_W;
  c.height = SPIRE_H;
  // Kept off the graphics card, as keptContext in paper.ts says why.
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  // Back to front: the ones further up the picture first.
  const shards = [...CLUSTERS[v]].sort((a, b) => a.dy - b.dy);
  for (const s of shards) shard(ctx, SPIRE_FOOT.x + s.dx, SPIRE_FOOT.y + s.dy, s);
  spires.set(v, c);
  return c;
}

/** One shard of ice, its foot's middle at (x, y): a lit west face, a shadowed east one, an edge and a glint. */
function shard(ctx: Ctx, x: number, y: number, s: Shard) {
  const tipX = x + s.lean, tipY = y - s.h;
  const left = x - s.half, right = x + s.half;
  // The west face, in the light.
  ctx.fillStyle = ICE.lit;
  ctx.beginPath();
  ctx.moveTo(left, y);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(x + s.lean / 2, y + 1);
  ctx.closePath();
  ctx.fill();
  // The east face, in its own shadow.
  ctx.fillStyle = ICE.shade;
  ctx.beginPath();
  ctx.moveTo(x + s.lean / 2, y + 1);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(right, y);
  ctx.closePath();
  ctx.fill();
  // A band of the ice's middle tone up the ridge between them.
  ctx.strokeStyle = ICE.mid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + s.lean / 2, y);
  ctx.lineTo(tipX, tipY + 2);
  ctx.stroke();
  // The outline, so a shard reads against the pale top behind it.
  ctx.strokeStyle = ICE.edge;
  ctx.beginPath();
  ctx.moveTo(left, y);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(right, y);
  ctx.stroke();
  // A glint near the tip, where the light catches it.
  ctx.fillStyle = ICE.glint;
  ctx.fillRect(Math.round(tipX - 1), Math.round(tipY + s.h * 0.25), 1, 2);
}
